/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  Checkbox,
  DatePicker,
} from 'antd';
import moment from 'moment';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getEstadosGlobales, getTiposDeEmpleados } from 'api/catalogos/index';
import { ESTATUS_ACTIVO } from 'utils/constants';
import {
  getPercepcionesExcluirPorTipoDeEmpleado,
  getPercepcionExcluirPorTipoDeEmpleado,
  postPercepcionExcluirPorTipoDeEmpleado,
  patchPercepcionExcluirPorTipoDeEmpleado,
  deletePercepcionExcluirPorTipoDeEmpleado,
} from 'api/catalogos/percepcionesExcluirPorTipoDeEmpleado';

const DATE_FORMAT = 'DD/MM/YYYY';

const PercepcionesExcluirPorTipoDeEmpleadoConfig = ({ percepcionId, permission, disabled }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentTableData, setCurrentTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [tiposEmpleados, setTiposEmpleados] = useState([]);
  const [disableFields, setDisableFields] = useState(false);

  const formInstances = [form];

  const fetchData = async () => {
    if (!percepcionId) return;
    setLoading(true);
    try {
      const _data = await getPercepcionesExcluirPorTipoDeEmpleado({ percepcion: percepcionId });
      setData(formatReceived(_data));
    } catch (err) {
      onError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setCurrentTableData([]);

    const fetchAll = async () => {
      setLoading(true);
      try {
        const [_estados, _tiposEmpleados] = await Promise.all([
          getEstadosGlobales(),
          getTiposDeEmpleados(),
        ]);
        if (mounted) {
          setEstados(_estados);
          setTiposEmpleados(_tiposEmpleados);
          if (percepcionId) {
            await fetchData();
          }
        }
      } catch (err) {
        onError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percepcionId]);

  const onCancel = () => {
    setVisible(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    try {
      const [key] = selectedRowKeys;
      const match = responseData || data.find((item) => item.id === key);
      const _record = await getPercepcionExcluirPorTipoDeEmpleado(match.id);
      if (_record) {
        form.resetFields();
        const formatted = formatReceived(_record);
        if (typeof formatted.tipo_de_empleado === 'object') {
          formatted.tipo_de_empleado = formatted.tipo_de_empleado?.id;
        }
        if (formatted.vigencia_inicial) {
          formatted.vigencia_inicial = moment(formatted.vigencia_inicial);
        }
        if (formatted.vigencia_final) {
          formatted.vigencia_final = moment(formatted.vigencia_final);
        }
        form.setFieldsValue(formatted);
        setDisableFields(_record?.estados_globales !== ESTATUS_ACTIVO);
        setVisible(true);
      }
    } catch (err) {
      onError(err);
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      values.percepcion = percepcionId;

      if (values.vigencia_inicial && moment.isMoment(values.vigencia_inicial)) {
        values.vigencia_inicial = values.vigencia_inicial.format('YYYY-MM-DD');
      }
      if (values.vigencia_final && moment.isMoment(values.vigencia_final)) {
        values.vigencia_final = values.vigencia_final.format('YYYY-MM-DD');
      } else if (!values.vigencia_final) {
        values.vigencia_final = null;
      }

      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchPercepcionExcluirPorTipoDeEmpleado(key, values)
        : postPercepcionExcluirPorTipoDeEmpleado(values));

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    if (!selectedRowKeys.length) return;
    try {
      setLoading(true);
      const deleted = await deletePercepcionExcluirPorTipoDeEmpleado(selectedRowKeys[0]);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    if (selectedRowKeys.includes(record.id)) {
      setSelectedRowKeys((prev) => prev.filter((k) => k !== record.id));
      setSelectedRows((prev) => prev.filter((row) => row.id !== record.id));
    } else {
      setSelectedRowKeys((prev) => [...prev, record.id]);
      setSelectedRows((prev) => [...prev, record]);
    }
  };

  const effectiveData = currentTableData.length ? currentTableData : data;

  const handleTableChange = (_pagination, _filters, _sorter, extra) => {
    setCurrentTableData(extra?.currentDataSource || []);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    columnTitle: (
      <Checkbox
        checked={selectedRowKeys.length > 0 && selectedRowKeys.length === effectiveData.length}
        indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < effectiveData.length}
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedRowKeys(effectiveData.map((row) => row.id));
            setSelectedRows([...effectiveData]);
          } else {
            setSelectedRowKeys([]);
            setSelectedRows([]);
          }
        }}
      />
    ),
    onSelect: (record, selected) => {
      if (selected) {
        setSelectedRowKeys((prev) => [...prev, record.id]);
        setSelectedRows((prev) => [...prev, record]);
      } else {
        setSelectedRowKeys((prev) => prev.filter((k) => k !== record.id));
        setSelectedRows((prev) => prev.filter((row) => row.id !== record.id));
      }
    },
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    form.setFieldsValue({ estados_globales: ESTATUS_ACTIVO });
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Tipo de Empleado',
      dataIndex: 'tipo_de_empleado',
      key: 'tipo_de_empleado',
      render: (val) => {
        if (typeof val === 'object') return val?.descripcion || val?.id;
        const te = tiposEmpleados.find((item) => item.id === val);
        return te ? te.descripcion : val;
      },
    },
    {
      titleText: 'Vigencia Inicial',
      dataIndex: 'vigencia_inicial',
      key: 'vigencia_inicial',
      width: 150,
      render: (val) => (val ? moment(val).format(DATE_FORMAT) : '—'),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
      render: (val) => (val ? moment(val).format(DATE_FORMAT) : 'Sin Fecha Final'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 130,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = { required: true, message: 'El campo es requerido' };

  return (
    <Row style={{ marginTop: 20 }}>
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          titleTable="Tipos de Empleado a Excluir"
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          onTableChange={handleTableChange}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          disabled={disabled || !percepcionId}
        />

        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          width={650}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Empleado a Excluir`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="formTipoEmpleadoExcluir"
            initialValues={{ estados_globales: ESTATUS_ACTIVO }}
          >
            <Row gutter={16}>
              <Col xs={24} md={24}>
                <Form.Item
                  name="tipo_de_empleado"
                  label="Tipo de Empleado a Excluir"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Select
                    dataSource={tiposEmpleados}
                    keyProp="id"
                    labelProp="descripcion"
                    showSearch
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="vigencia_inicial"
                  label="Vigencia Inicial"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <DatePicker
                    format={DATE_FORMAT}
                    style={{ width: '100%' }}
                    disabled={disableFields}
                    placeholder="dd/mm/aaaa"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="vigencia_final"
                  label="Vigencia Final"
                  hasFeedback
                >
                  <DatePicker
                    format={DATE_FORMAT}
                    style={{ width: '100%' }}
                    disabled={disableFields}
                    placeholder="Sin Fecha Final"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Select
                    dataSource={estados}
                    disabled={selectedRowKeys.length <= 0 || disableFields}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="¿Estás seguro de eliminar este Tipo de Empleado de la lista de exclusión?"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PercepcionesExcluirPorTipoDeEmpleadoConfig;
